import { useMemo, useState } from 'react';

/**
 * Tiny client-side sort hook for simple HTML tables.
 *
 * Usage:
 *   const { sortedItems, sortKey, sortDir, setSort } = useTableSort(items, {
 *     defaultKey: 'lastName',
 *     defaultDir: 'asc',
 *     accessors: {
 *       lastName: (row) => row.lastName,
 *       clubNumber: (row) => row.clubAthleteNumber,
 *     },
 *   });
 *   ...
 *   <th onClick={() => setSort('lastName')}>Name {sortIndicator('lastName')}</th>
 *
 * Numbers and dates sort naturally; strings sort case-insensitively.
 * Null/undefined sort last regardless of direction.
 */
export default function useTableSort(items, { defaultKey, defaultDir = 'asc', accessors }) {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const setSort = (key) => {
    if (!accessors[key]) return; // unknown / not sortable
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    const accessor = accessors[sortKey];
    if (!accessor) return items;

    const dir = sortDir === 'asc' ? 1 : -1;

    return [...items].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);

      // Nulls always last.
      const aNull = av === null || av === undefined || av === '';
      const bNull = bv === null || bv === undefined || bv === '';
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;

      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }

      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
  }, [items, sortKey, sortDir, accessors]);

  return { sortedItems, sortKey, sortDir, setSort };
}
